import React, { useRef, useEffect, useState, Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, TransformControls, Grid, Environment, ContactShadows } from '@react-three/drei';
import * as THREE from 'three';
import {
  FaMousePointer, FaHandPaper, FaSquare, FaCircle, FaFont, FaPen,
  FaUndo, FaSave, FaLayerGroup,
  FaVectorSquare, FaCog, FaSearchPlus, FaCompress,
  FaCube, FaShapes, FaRulerCombined, FaMagnet, FaPlay, FaPause, FaStop,
  FaMusic, FaVolumeUp, FaFolderOpen, FaCloudUploadAlt, FaTrash
} from 'react-icons/fa';

// --- Types ---

type Mode = '2D' | '3D' | 'Audio';
type ToolType2D = 'select' | 'pan' | 'rect' | 'circle' | 'line' | 'text' | 'pen' | 'measure';
type ToolType3D = 'select' | 'translate' | 'rotate' | 'scale';

// Fix for NodeJS namespace issue in browser environment
type Timer = ReturnType<typeof setInterval>;

interface Point {
  x: number;
  y: number;
}

// 2D Element
interface Element2D {
  id: string;
  type: ToolType2D;
  x: number;
  y: number;
  width?: number;
  height?: number;
  points?: Point[];
  text?: string;
  fill: string;
  stroke: string;
  strokeWidth: number;
  opacity: number;
  rotation: number;
  zIndex: number;
}

// 3D Element
interface Element3D {
  id: string;
  type: 'cube' | 'sphere' | 'plane';
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
  color: string;
}

// Audio Track
interface AudioTrack {
  id: string;
  name: string;
  color: string;
  clips: { start: number; duration: number; name: string; audioUrl?: string }[];
  volume: number;
  muted: boolean;
}

interface WhiteboardProps {
  isActive?: boolean;
  onActivate?: () => void;
  onDeactivate?: () => void;
}

// --- Constants ---

const GRID_SIZE = 20;

// --- Helper Components ---

const ToolbarButton: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  tooltip: string;
  color?: string;
}> = ({ active, onClick, icon, tooltip, color = 'bg-[#00D4FF]' }: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  tooltip: string;
  color?: string;
}) => (
    <button
      onClick={onClick}
      title={tooltip}
      className={`p-3 rounded-lg transition-all duration-200 flex items-center justify-center text-lg relative group ${active
        ? `${color} text-[#09090b] shadow-[0_0_15px_rgba(0,212,255,0.5)] scale-105 z-10`
        : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
        }`}
    >
      {icon}
      {/* Tooltip */}
      <span className="absolute left-14 bg-black px-2 py-1 rounded text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap border border-white/10 z-50">
        {tooltip}
      </span>
    </button>
  );

const ModeSwitcher: React.FC<{ mode: Mode; setMode: (m: Mode) => void }> = ({ mode, setMode }: { mode: Mode; setMode: (m: Mode) => void }) => (
  <div className="flex bg-[#121214] p-1 rounded-xl border border-white/10 mb-4 shadow-lg shrink-0">
    {(['2D', '3D', 'Audio'] as Mode[]).map(m => (
      <button
        key={m}
        onClick={() => setMode(m)}
        className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${mode === m
          ? 'bg-gradient-to-r from-[#00D4FF] to-[#00A3FF] text-black shadow-lg'
          : 'text-white/50 hover:text-white hover:bg-white/5'
          }`}
      >
        {m === '2D' && 'AutoCAD'}
        {m === '3D' && 'Blender 3D'}
        {m === 'Audio' && 'Audition'}
      </button>
    ))}
  </div>
);

// --- 3D Scene Component ---
const Scene3D: React.FC<{
  elements: Element3D[];
  setElements: React.Dispatch<React.SetStateAction<Element3D[]>>;
  selectedId: string | null;
  setSelectedId: (id: string | null) => void;
  tool: ToolType3D;
}> = ({ elements, setElements, selectedId, setSelectedId, tool }: {
  elements: Element3D[];
  setElements: React.Dispatch<React.SetStateAction<Element3D[]>>;
  selectedId: string | null;
  setSelectedId: (id: string | null) => void;
  tool: ToolType3D;
}) => {
    return (
      <>
        <ambientLight intensity={0.5} />
        <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} intensity={1} castShadow />
        <Environment preset="city" />

        <Grid infiniteGrid sectionColor="#444" cellColor="#222" fadeDistance={30} />

        <group>
          {elements.map(el => (
            <MeshElement
              key={el.id}
              el={el}
              isSelected={selectedId === el.id}
              onSelect={() => setSelectedId(el.id)}
              tool={tool}
              onUpdate={(newProps) => {
                setElements(prev => prev.map(item => item.id === el.id ? { ...item, ...newProps } : item));
              }}
            />
          ))}
        </group>

        <OrbitControls makeDefault />
        <ContactShadows position={[0, -0.01, 0]} color="#000000" resolution={512} opacity={0.4} blur={2.5} />
      </>
    );
  };

const MeshElement: React.FC<{
  el: Element3D;
  isSelected: boolean;
  onSelect: () => void;
  tool: ToolType3D;
  onUpdate: (props: Partial<Element3D>) => void;
}> = ({ el, isSelected, onSelect, tool, onUpdate }: {
  el: Element3D;
  isSelected: boolean;
  onSelect: () => void;
  tool: ToolType3D;
  onUpdate: (props: Partial<Element3D>) => void;
}) => {
    const meshRef = useRef<THREE.Mesh>(null);

    // Safety check just in case arrays are malformed from JSON
    const pos = Array.isArray(el.position) && el.position.length === 3 ? el.position : [0, 0, 0];
    const rot = Array.isArray(el.rotation) && el.rotation.length === 3 ? el.rotation : [0, 0, 0];
    const scl = Array.isArray(el.scale) && el.scale.length === 3 ? el.scale : [1, 1, 1];

    return (
      <>
        {isSelected && tool !== 'select' && (
          <TransformControls
            object={meshRef}
            mode={tool}
            onObjectChange={() => {
              if (meshRef.current) {
                onUpdate({
                  position: meshRef.current.position.toArray(),
                  rotation: meshRef.current.rotation.toArray().slice(0, 3) as [number, number, number],
                  scale: meshRef.current.scale.toArray()
                });
              }
            }}
          />
        )}

        <mesh
          ref={meshRef}
          position={pos}
          rotation={rot}
          scale={scl}
          onClick={(e: any) => { e.stopPropagation(); onSelect(); }}
        >
          {el.type === 'cube' && <boxGeometry />}
          {el.type === 'sphere' && <sphereGeometry />}
          {el.type === 'plane' && <planeGeometry args={[5, 5]} rotation={[-Math.PI / 2, 0, 0]} />}
          <meshStandardMaterial
            color={isSelected ? '#00D4FF' : el.color}
            emissive={isSelected ? '#004466' : '#000000'}
            roughness={0.2}
            metalness={0.8}
          />
        </mesh>
      </>
    );
  };

// --- Main Component ---

const Whiteboard: React.FC<WhiteboardProps> = ({ isActive = true, onActivate, onDeactivate }) => {
  // --- Global State ---
  const [mode, setMode] = useState<Mode>('2D');

  // --- 2D State ---
  const [elements2D, setElements2D] = useState<Element2D[]>([]);
  const [selectedIds2D, setSelectedIds2D] = useState<Set<string>>(new Set());
  const [tool2D, setTool2D] = useState<ToolType2D>('select');
  const [scale2D, setScale2D] = useState(1);
  const [pan2D, setPan2D] = useState({ x: 0, y: 0 });
  const [isSnapping, setIsSnapping] = useState(true);

  // --- 3D State ---
  const [elements3D, setElements3D] = useState<Element3D[]>([
    { id: 'cube1', type: 'cube', position: [0, 0.5, 0], rotation: [0, 0, 0], scale: [1, 1, 1], color: '#6366f1' }
  ]);
  const [selectedId3D, setSelectedId3D] = useState<string | null>(null);
  const [tool3D, setTool3D] = useState<ToolType3D>('translate');

  // --- Audio State ---
  const [tracks, setTracks] = useState<AudioTrack[]>([
    { id: 't1', name: 'Vocals', color: '#10b981', volume: 0.8, muted: false, clips: [{ start: 2, duration: 4, name: 'Vox_Take1.wav' }] },
    { id: 't2', name: 'Drums', color: '#f59e0b', volume: 1.0, muted: false, clips: [{ start: 0, duration: 8, name: 'Loop_Drum.wav' }] },
    { id: 't3', name: 'Bass', color: '#6366f1', volume: 0.9, muted: false, clips: [{ start: 0, duration: 8, name: 'Bass_Line.wav' }] },
    { id: 't4', name: 'FX', color: '#ec4899', volume: 0.6, muted: false, clips: [{ start: 6, duration: 1, name: 'Impact.wav' }] },
  ]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  // --- I/O Refs ---
  const fileInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);

  // --- Export/Import Logic ---
  const handleExport = () => {
    const project = {
      version: 'PRO-STUDIO-v1',
      timestamp: Date.now(),
      mode,
      data: {
        elements2D,
        elements3D,
        tracks
      }
    };
    const blob = new Blob([JSON.stringify(project, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `design-project-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const json = JSON.parse(ev.target?.result as string);
        if (json.version && json.data) {
          setElements2D(json.data.elements2D || []);
          setElements3D(json.data.elements3D || []);
          setTracks(json.data.tracks || []);
          // setMode(json.mode || '2D');
          alert('Project loaded successfully!');
        } else {
          alert('Invalid project file format.');
        }
      } catch (err) {
        console.error(err);
        alert('Failed to parse project file.');
      }
    };
    reader.readAsText(file);
  };

  const handleAudioUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const url = URL.createObjectURL(file);
    const audio = new Audio(url);
    audio.onloadedmetadata = () => {
      const duration = audio.duration;
      const newTrack: AudioTrack = {
        id: Math.random().toString(36),
        name: file.name,
        color: '#' + Math.floor(Math.random() * 16777215).toString(16),
        volume: 1.0,
        muted: false,
        clips: [{ start: 0, duration: duration, name: file.name, audioUrl: url }]
      };
      setTracks(prev => [...prev, newTrack]);
    };
  };

  // --- 2D Logic ---
  const canvasRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<Point | null>(null);
  const [currentElement, setCurrentElement] = useState<Element2D | null>(null);

  // Snapping Helper
  const snapToGrid = (val: number) => isSnapping ? Math.round(val / GRID_SIZE) * GRID_SIZE : val;

  const screenToWorld = (sx: number, sy: number) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: (sx - rect.left - pan2D.x) / scale2D,
      y: (sy - rect.top - pan2D.y) / scale2D
    };
  };

  const handleMouseDown2D = (e: React.MouseEvent) => {
    if (tool2D === 'pan') {
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
      return;
    }

    const rawPos = screenToWorld(e.clientX, e.clientY);
    const pos = { x: snapToGrid(rawPos.x), y: snapToGrid(rawPos.y) };

    if (tool2D === 'select') {
      const clicked = elements2D.slice().reverse().find(el => {
        // Hit Testing Logic
        if (el.type === 'rect') return pos.x >= el.x && pos.x <= el.x + (el.width || 0) && pos.y >= el.y && pos.y <= el.y + (el.height || 0);
        if (el.type === 'circle') {
          const r = (el.width || 0) / 2;
          const cx = el.x + r;
          const cy = el.y + r;
          return Math.sqrt((pos.x - cx) ** 2 + (pos.y - cy) ** 2) <= r;
        }
        return false;
      });

      if (clicked) {
        setSelectedIds2D(new Set([clicked.id]));
        setIsDragging(true);
        setDragStart(pos);
      } else {
        setSelectedIds2D(new Set());
      }
      return;
    }

    if (tool2D === 'measure') {
      // Just for visual, handled in rendering
      return;
    }

    // Creation
    setIsDragging(true);
    setDragStart(pos);

    const newEl: Element2D = {
      id: Math.random().toString(36).substr(2, 9),
      type: tool2D,
      x: pos.x,
      y: pos.y,
      width: 0,
      height: 0,
      fill: '#2a2a2e',
      stroke: '#00D4FF',
      strokeWidth: 2,
      opacity: 1,
      rotation: 0,
      zIndex: elements2D.length + 1,
      points: tool2D === 'pen' ? [pos] : undefined
    };
    setCurrentElement(newEl);
  };

  const handleMouseMove2D = (e: React.MouseEvent) => {
    if (!isDragging) return;

    if (tool2D === 'pan' && dragStart) {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      setPan2D(prev => ({ x: prev.x + dx, y: prev.y + dy }));
      setDragStart({ x: e.clientX, y: e.clientY });
      return;
    }

    const rawPos = screenToWorld(e.clientX, e.clientY);
    const pos = { x: snapToGrid(rawPos.x), y: snapToGrid(rawPos.y) };

    if (tool2D === 'select' && dragStart && selectedIds2D.size > 0) {
      const dx = pos.x - dragStart.x;
      const dy = pos.y - dragStart.y;
      setElements2D(prev => prev.map(el => {
        if (selectedIds2D.has(el.id)) {
          return { ...el, x: el.x + dx, y: el.y + dy };
        }
        return el;
      }));
      setDragStart(pos);
      return;
    }

    if (currentElement && dragStart) {
      const activeEl = { ...currentElement };
      if (tool2D === 'rect' || tool2D === 'text') {
        activeEl.width = pos.x - dragStart.x;
        activeEl.height = pos.y - dragStart.y;
        if (activeEl.width < 0) { activeEl.x = pos.x; activeEl.width = Math.abs(activeEl.width); }
        if (activeEl.height < 0) { activeEl.y = pos.y; activeEl.height = Math.abs(activeEl.height); }
      } else if (tool2D === 'circle') {
        const d = Math.max(Math.abs(pos.x - dragStart.x), Math.abs(pos.y - dragStart.y));
        activeEl.width = d; activeEl.height = d;
        if (pos.x < dragStart.x) activeEl.x = dragStart.x - d;
        if (pos.y < dragStart.y) activeEl.y = dragStart.y - d;
      } else if (tool2D === 'pen') {
        activeEl.points = [...(activeEl.points || []), pos];
      }
      setCurrentElement(activeEl);
    }
  };

  const handleMouseUp2D = () => {
    setIsDragging(false);
    setDragStart(null);
    if (currentElement) {
      setElements2D(prev => [...prev, currentElement]);
      setSelectedIds2D(new Set([currentElement.id]));
      setTool2D('select');
      setCurrentElement(null);
    }
  };

  const handleWheel2D = (e: React.WheelEvent) => {
    const ZOOM_SENSITIVITY = 0.001;
    const newScale = Math.min(Math.max(scale2D - e.deltaY * ZOOM_SENSITIVITY, 0.1), 5);
    if (canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Calculate world point before zoom
      const worldX = (mouseX - pan2D.x) / scale2D;
      const worldY = (mouseY - pan2D.y) / scale2D;

      // Update pan to keep world point at same mouse position
      const newPanX = mouseX - worldX * newScale;
      const newPanY = mouseY - worldY * newScale;

      setPan2D({ x: newPanX, y: newPanY });
      setScale2D(newScale);
    }
  };

  // --- Audio Logic ---
  useEffect(() => {
    let interval: Timer;
    if (isPlaying) {
      interval = setInterval(() => {
        setCurrentTime(t => (t + 0.1) % 20); // Loop 20s
      }, 100);
    }
    return () => clearInterval(interval);
  }, [isPlaying]);

  // --- Renderers ---
  if (!isActive && onActivate) {
    return (
      <div className="h-full w-full bg-[#09090b] rounded-lg border border-white/10 p-8 flex flex-col items-center justify-center text-center">
        <div className="flex gap-4 mb-6">
          <div className="w-16 h-16 rounded-xl bg-blue-500/20 flex items-center justify-center text-blue-400 text-3xl"><FaVectorSquare /></div>
          <div className="w-16 h-16 rounded-xl bg-purple-500/20 flex items-center justify-center text-purple-400 text-3xl"><FaCube /></div>
          <div className="w-16 h-16 rounded-xl bg-green-500/20 flex items-center justify-center text-green-400 text-3xl"><FaMusic /></div>
        </div>
        <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-white/60 mb-3">Creative Suite Enterprise</h2>
        <p className="text-white/50 max-w-md mb-8">
          The ultimate integrated environment for 2D Drafting, 3D Modeling, and Audio Production.
        </p>
        <button onClick={onActivate} className="px-8 py-3 bg-[#00D4FF] text-[#09090b] font-bold rounded-lg hover:scale-105 transition-all shadow-[0_0_20px_rgba(0,212,255,0.4)]">
          Launch Studio
        </button>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col bg-[#09090b] overflow-hidden select-none">
      {/* Top Bar - Studio/Mode Switcher */}
      <div className="h-14 bg-[#121214] border-b border-white/10 flex items-center justify-between px-4 shrink-0 z-30">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[#00D4FF] font-bold text-lg">PRO-STUDIO</span>
          <div className="h-4 w-[1px] bg-white/20 mx-2" />
          <span className="text-xs text-white/50 uppercase tracking-widest">{mode} WORKSPACE</span>
        </div>

        <ModeSwitcher mode={mode} setMode={setMode} />

        <div className="flex items-center gap-2">
          <input type="file" ref={fileInputRef} onChange={handleImport} className="hidden" accept=".json" />
          <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-md text-sm text-white/80 transition-colors">
            <FaFolderOpen /> Open
          </button>
          <button onClick={handleExport} className="flex items-center gap-2 px-3 py-1.5 bg-[#00D4FF]/10 hover:bg-[#00D4FF]/20 text-[#00D4FF] rounded-md text-sm font-bold transition-colors border border-[#00D4FF]/20">
            <FaSave /> Save
          </button>
          <div className="w-[1px] h-6 bg-white/10 mx-2" />
          {onDeactivate && <button onClick={onDeactivate} className="p-2 hover:bg-white/10 rounded-lg text-white/60"><FaCompress /></button>}
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* 2D MODE */}
        {mode === '2D' && (
          <>
            {/* Toolbar 2D */}
            <div className="w-16 bg-[#121214] border-r border-white/10 flex flex-col items-center py-4 gap-3 z-20 shadow-2xl">
              <ToolbarButton active={tool2D === 'select'} onClick={() => setTool2D('select')} icon={<FaMousePointer />} tooltip="Select" />
              <ToolbarButton active={tool2D === 'pan'} onClick={() => setTool2D('pan')} icon={<FaHandPaper />} tooltip="Pan" />
              <div className="w-8 h-[1px] bg-white/10 my-1" />
              <ToolbarButton active={tool2D === 'rect'} onClick={() => setTool2D('rect')} icon={<FaSquare />} tooltip="Rectangle" />
              <ToolbarButton active={tool2D === 'circle'} onClick={() => setTool2D('circle')} icon={<FaCircle />} tooltip="Circle" />
              <ToolbarButton active={tool2D === 'pen'} onClick={() => setTool2D('pen')} icon={<FaPen />} tooltip="Pen" />
              <ToolbarButton active={tool2D === 'text'} onClick={() => setTool2D('text')} icon={<FaFont />} tooltip="Text" />
              <ToolbarButton active={tool2D === 'measure'} onClick={() => setTool2D('measure')} icon={<FaRulerCombined />} tooltip="Measure" />
              <div className="flex-1" />
              <button onClick={() => setIsSnapping(!isSnapping)} className={`p-2 rounded ${isSnapping ? 'bg-cyan-500/20 text-cyan-400' : 'text-white/30'}`} title="Snap to Grid"><FaMagnet /></button>
            </div>

            {/* Canvas 2D */}
            <div className="flex-1 relative overflow-hidden cursor-crosshair bg-[#09090b]" ref={canvasRef}
              onMouseDown={handleMouseDown2D} onMouseMove={handleMouseMove2D} onMouseUp={handleMouseUp2D} onMouseLeave={handleMouseUp2D} onWheel={handleWheel2D}>

              <div className="absolute inset-0 pointer-events-none opacity-20"
                style={{
                  backgroundImage: `radial-gradient(circle, #444 1px, transparent 1px)`,
                  backgroundSize: `${GRID_SIZE * scale2D}px ${GRID_SIZE * scale2D}px`,
                  backgroundPosition: `${pan2D.x}px ${pan2D.y}px`
                }}
              />

              <div className="absolute top-0 left-0 w-full h-full pointer-events-none origin-top-left"
                style={{ transform: `translate(${pan2D.x}px, ${pan2D.y}px) scale(${scale2D})` }}>
                {/* Rendering Elements */}
                {elements2D.map(el => (
                  <div key={el.id} style={{
                    position: 'absolute', left: el.x, top: el.y, width: el.width, height: el.height,
                    border: `${el.strokeWidth}px solid ${el.stroke}`, backgroundColor: el.fill, opacity: el.opacity,
                    borderRadius: el.type === 'circle' ? '50%' : 0,
                    boxShadow: selectedIds2D.has(el.id) ? '0 0 0 2px #00D4FF' : 'none'
                  }}>
                    {el.type === 'rect' && selectedIds2D.has(el.id) && (
                      /* Dimensions */
                      <div className="absolute -top-6 left-0 text-xs bg-[#00D4FF] text-black px-1 rounded font-mono">
                        {Math.round(el.width || 0)} x {Math.round(el.height || 0)}
                      </div>
                    )}
                  </div>
                ))}
                {currentElement && (
                  <div style={{
                    position: 'absolute', left: currentElement.x, top: currentElement.y, width: currentElement.width, height: currentElement.height,
                    border: '2px dashed #00D4FF', backgroundColor: 'rgba(0,212,255,0.1)'
                  }} />
                )}
              </div>

              {/* Stats */}
              <div className="absolute bottom-4 left-4 bg-black/50 backdrop-blur px-3 py-1 rounded text-xs font-mono text-white/50">
                X: {pan2D.x.toFixed(0)} Y: {pan2D.y.toFixed(0)} | Zoom: {(scale2D * 100).toFixed(0)}%
              </div>
            </div>

            {/* Right Properties Panel 2D */}
            <div className="w-72 bg-[#121214] border-l border-white/10 flex flex-col z-20 shadow-2xl p-4">
              <div className="pb-4 border-b border-white/10 font-bold flex items-center gap-2 mb-4">
                <FaCog /> Properties
              </div>

              {selectedIds2D.size === 1 ? (
                (() => {
                  const selectedEl = elements2D.find(e => selectedIds2D.has(e.id));
                  if (!selectedEl) return null;
                  return (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-xs text-white/50 uppercase">Position</label>
                        <div className="grid grid-cols-2 gap-2">
                          <div><label className="text-xs">X</label> <input type="number" readOnly value={Math.round(selectedEl.x)} className="w-full bg-white/5 rounded p-1 text-sm" /></div>
                          <div><label className="text-xs">Y</label> <input type="number" readOnly value={Math.round(selectedEl.y)} className="w-full bg-white/5 rounded p-1 text-sm" /></div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs text-white/50 uppercase">Style</label>
                        <div className="flex justify-between items-center">
                          <span>Fill</span>
                          <input type="color" value={selectedEl.fill} onChange={e => setElements2D(prev => prev.map(el => el.id === selectedEl.id ? { ...el, fill: e.target.value } : el))} />
                        </div>
                        <div className="flex justify-between items-center">
                          <span>Stroke</span>
                          <input type="color" value={selectedEl.stroke} onChange={e => setElements2D(prev => prev.map(el => el.id === selectedEl.id ? { ...el, stroke: e.target.value } : el))} />
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setElements2D(prev => prev.filter(el => el.id !== selectedEl.id));
                          setSelectedIds2D(new Set());
                        }}
                        className="w-full py-2 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 flex items-center justify-center gap-2"
                      >
                        <FaTrash /> Delete
                      </button>
                    </div>
                  );
                })()
              ) : (
                <div className="text-white/30 text-center italic text-sm mt-10">Select an object to edit properties</div>
              )}

              {/* Layers */}
              <div className="mt-8 pt-4 border-t border-white/10 flex-1">
                <h4 className="text-xs font-bold uppercase tracking-wider text-white/40 flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2"><FaLayerGroup /> Layers ({elements2D.length})</div>
                </h4>
                <div className="space-y-1 overflow-y-auto h-48">
                  {elements2D.slice().reverse().map((el, i) => (
                    <div key={el.id} onClick={() => setSelectedIds2D(new Set([el.id]))} className={`p-2 rounded text-xs cursor-pointer truncate ${selectedIds2D.has(el.id) ? 'bg-[#00D4FF]/20 text-cyan-300' : 'hover:bg-white/5'}`}>
                      {el.type} - {el.id.substr(0, 4)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {/* 3D MODE */}
        {mode === '3D' && (
          <>
            {/* Toolbar 3D */}
            <div className="w-16 bg-[#121214] border-r border-white/10 flex flex-col items-center py-4 gap-3 z-20 shadow-2xl">
              <ToolbarButton active={tool3D === 'select'} onClick={() => setTool3D('select')} icon={<FaMousePointer />} tooltip="Select" />
              <ToolbarButton active={tool3D === 'translate'} onClick={() => setTool3D('translate')} icon={<FaShapes />} tooltip="Move" />
              <ToolbarButton active={tool3D === 'rotate'} onClick={() => setTool3D('rotate')} icon={<FaUndo />} tooltip="Rotate" />
              <ToolbarButton active={tool3D === 'scale'} onClick={() => setTool3D('scale')} icon={<FaSearchPlus />} tooltip="Scale" />
              <div className="w-8 h-[1px] bg-white/10 my-1" />
              <ToolbarButton active={false} onClick={() => setElements3D(p => [...p, { id: Math.random().toString(), type: 'cube', position: [0, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1], color: '#6366f1' }])} icon={<FaCube />} tooltip="Add Cube" />
              <ToolbarButton active={false} onClick={() => setElements3D(p => [...p, { id: Math.random().toString(), type: 'sphere', position: [2, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1], color: '#10b981' }])} icon={<FaCircle />} tooltip="Add Sphere" />
            </div>

            <div className="flex-1 bg-black relative">
              <Suspense fallback={<div className="text-white p-10">Loading 3D Engine...</div>}>
                <Canvas shadows camera={{ position: [5, 5, 5], fov: 50 }}>
                  <Scene3D
                    elements={elements3D}
                    setElements={setElements3D}
                    selectedId={selectedId3D}
                    setSelectedId={setSelectedId3D}
                    tool={tool3D}
                  />
                </Canvas>
              </Suspense>
              <div className="absolute top-4 right-4 bg-black/80 p-2 rounded text-xs font-mono text-green-400">
                BLENDER ENGINE V2.9
              </div>
            </div>

            {/* Right Properties Panel 3D */}
            <div className="w-72 bg-[#121214] border-l border-white/10 flex flex-col z-20 shadow-2xl p-4">
              <div className="pb-4 border-b border-white/10 font-bold flex items-center gap-2 mb-4">
                <FaCog /> Properties
              </div>

              {selectedId3D ? (
                (() => {
                  const selectedEl = elements3D.find(e => e.id === selectedId3D);
                  if (!selectedEl) return null;
                  return (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-xs text-white/50 uppercase">Type</label>
                        <div className="font-mono text-cyan-400 capitalize">{selectedEl.type}</div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs text-white/50 uppercase">Color</label>
                        <input type="color" value={selectedEl.color} onChange={e => setElements3D(prev => prev.map(el => el.id === selectedEl.id ? { ...el, color: e.target.value } : el))} className="w-full h-8 cursor-pointer" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs text-white/50 uppercase">Transform</label>
                        <div className="text-xs text-white/40">Use Gizmos in viewport to transform</div>
                        <div className="grid grid-cols-3 gap-1 text-center font-mono text-[10px]">
                          <div className="bg-white/5 rounded p-1">X: {selectedEl.position[0].toFixed(1)}</div>
                          <div className="bg-white/5 rounded p-1">Y: {selectedEl.position[1].toFixed(1)}</div>
                          <div className="bg-white/5 rounded p-1">Z: {selectedEl.position[2].toFixed(1)}</div>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setElements3D(prev => prev.filter(el => el.id !== selectedEl.id));
                          setSelectedId3D(null);
                        }}
                        className="w-full py-2 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 flex items-center justify-center gap-2 mt-4"
                      >
                        <FaTrash /> Delete Model
                      </button>
                    </div>
                  );
                })()
              ) : (
                <div className="text-white/30 text-center italic text-sm mt-10">Select an object to edit properties</div>
              )}
            </div>
          </>
        )}

        {/* AUDIO MODE */}
        {mode === 'Audio' && (
          <div className="flex-1 flex flex-col bg-[#1e1e24] overflow-hidden">
            {/* Audio Header */}
            <div className="h-16 border-b border-black/40 flex items-center px-4 bg-[#27272a] shadow-md z-10 justify-between">
              <div className="flex gap-4">
                <button onClick={() => setIsPlaying(!isPlaying)} className={`w-12 h-12 rounded-full flex items-center justify-center text-xl shadow-lg transition-transform hover:scale-105 active:scale-95 ${isPlaying ? 'bg-red-500 text-white' : 'bg-[#00D4FF] text-black'}`}>
                  {isPlaying ? <FaPause /> : <FaPlay className="ml-1" />}
                </button>
                <button onClick={() => { setIsPlaying(false); setCurrentTime(0); }} className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20">
                  <FaStop />
                </button>
                <div className="w-[1px] h-8 bg-white/10 mx-2" />
                <input type="file" ref={audioInputRef} onChange={handleAudioUpload} className="hidden" accept="audio/*" />
                <button onClick={() => audioInputRef.current?.click()} className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-full text-white/70 text-sm border border-white/5">
                  <FaCloudUploadAlt /> Import Audio
                </button>
              </div>
              <div className="bg-black/40 px-6 py-2 rounded-lg font-mono text-cyan-400 text-2xl tracking-widest border border-white/5 shadow-inner">
                00:00:{currentTime.toFixed(2).padStart(5, '0')}
              </div>
              <div className="flex gap-2 text-white/50 text-xs">
                <div>44.1 kHz</div>
                <div>16-bit</div>
                <div>Stereo</div>
              </div>
            </div>

            {/* Tracks Area */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden relative bg-[#18181b]">
              {/* Time Ruler */}
              <div className="h-8 bg-[#27272a] border-b border-black/40 flex items-end pl-48 pr-4 sticky top-0 z-20">
                {Array.from({ length: 20 }).map((_, i) => (
                  <div key={i} className="flex-1 border-l border-white/20 h-4 text-[10px] text-white/40 pl-1 font-mono">
                    00:0{i}
                  </div>
                ))}
              </div>

              {/* Playhead */}
              <div className="absolute top-8 bottom-0 w-[2px] bg-red-500 z-30 pointer-events-none transition-all duration-100 ease-linear shadow-[0_0_10px_red]"
                style={{ left: `calc(192px + ${(currentTime / 20) * (100 - 15)}%)` }} />

              {/* Track List */}
              <div className="pb-20">
                {tracks.map(track => (
                  <div key={track.id} className="h-32 flex border-b border-black/40 bg-[#1e1e24]">
                    {/* Track Controls (Left) */}
                    <div className="w-48 bg-[#27272a] border-r border-black/40 p-3 flex flex-col gap-2 shrink-0 relative">
                      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: track.color }} />
                      <div className="font-bold text-sm text-white/90 truncate">{track.name}</div>
                      <div className="flex items-center gap-2">
                        <FaVolumeUp className="text-xs text-white/40" />
                        <div className="h-1 bg-white/10 flex-1 rounded-full overflow-hidden">
                          <div className="h-full bg-[#00D4FF]" style={{ width: `${track.volume * 100}%` }} />
                        </div>
                      </div>
                      <div className="flex gap-2 text-xs mt-auto">
                        <button className="px-2 py-1 bg-white/5 rounded text-white/60 hover:text-white border border-white/5">M</button>
                        <button className="px-2 py-1 bg-white/5 rounded text-white/60 hover:text-white border border-white/5">S</button>
                        <button className="px-2 py-1 bg-red-500/20 text-red-500 rounded border border-red-500/30">REC</button>
                      </div>
                    </div>

                    {/* Clips Area */}
                    <div className="flex-1 relative bg-[#131315]">
                      {/* Grid Lines */}
                      <div className="absolute inset-0 flex pointer-events-none">
                        {Array.from({ length: 20 }).map((_, i) => (
                          <div key={i} className="flex-1 border-r border-white/5 h-full" />
                        ))}
                      </div>

                      {/* Clips */}
                      {track.clips.map((clip, i) => (
                        <div key={i} className="absolute top-1 bottom-1 rounded-lg border border-white/10 overflow-hidden cursor-move hover:brightness-110 transition-all font-mono group"
                          style={{
                            left: `${(clip.start / 20) * 100}%`,
                            width: `${(clip.duration / 20) * 100}%`,
                            backgroundColor: `${track.color}40`,
                            borderColor: track.color
                          }}>
                          {/* Waveform Fake */}
                          <div className="absolute inset-0 flex items-center gap-[2px] opacity-60 px-2 overflow-hidden">
                            {Array.from({ length: 50 }).map((_, wi) => (
                              <div key={wi} className="w-1 bg-white rounded-full" style={{ height: `${20 + Math.random() * 60}%`, opacity: 0.5 + Math.random() * 0.5 }} />
                            ))}
                          </div>
                          <div className="absolute top-1 left-2 text-[10px] font-bold text-white shadow-sm truncate max-w-full flex items-center gap-2">
                            {clip.name}
                          </div>

                          {/* Real Audio Element Hook */}
                          {clip.audioUrl && (
                            <audio
                              src={clip.audioUrl}
                              ref={(el) => {
                                if (el && isPlaying) {
                                  const offset = currentTime - clip.start;
                                  if (offset >= 0 && offset < clip.duration) {
                                    if (el.paused) {
                                      el.currentTime = offset;
                                      el.play().catch(e => console.log('Audio error:', e));
                                    }
                                  } else {
                                    if (!el.paused) el.pause();
                                  }
                                } else if (el) {
                                  el.pause();
                                }
                              }}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Contextual Footer (Status Bar) */}
      <div className="h-6 bg-[#09090b] border-t border-white/10 flex items-center px-4 text-[10px] font-mono text-white/40 justify-between shrink-0">
        <div>
          {mode === '2D' && `SELECTED: ${selectedIds2D.size} OBJECTS | GRID: ${GRID_SIZE}px | SNAP: ${isSnapping ? 'ON' : 'OFF'}`}
          {mode === '3D' && `OBJECTS: ${elements3D.length} | CAMERA: ORBIT | ENGINE: READY`}
          {mode === 'Audio' && `TRACKS: ${tracks.length} | SAMPLE RATE: 44.1KHZ | LATENCY: 12ms`}
        </div>
        <div>MEMORY: 128MB | CPU: 4%</div>
      </div>
    </div>
  );
};

export default Whiteboard;
